import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Mail, Phone, MapPin, Instagram, Send, CheckCircle2, Loader2 } from "lucide-react";
import { CONTACT } from "../data/site";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfoRow = ({ icon: Icon, label, value, href }) => {
  const content = (
    <div className="flex items-start gap-4 group">
      <div className="w-11 h-11 rounded-xl bg-[#5CC528]/10 flex items-center justify-center shrink-0 group-hover:bg-[#5CC528] transition-colors duration-300">
        <Icon size={19} className="text-[#5CC528] group-hover:text-black transition-colors duration-300" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-1">{label}</div>
        <div className="text-white group-hover:text-[#5CC528] transition-colors duration-300">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">{content}</a>
  ) : content;
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Lütfen ad, e-posta ve mesaj alanlarını doldurun.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Mesajınız gönderildi! En kısa sürede size dönüş yapacağız.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin veya bizi telefonla arayın.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-neutral-600 focus:border-[#5CC528] focus:outline-none focus:ring-1 focus:ring-[#5CC528] transition-colors duration-200";

  return (
    <>
      <PageHeader
        eyebrow="İletişim"
        titleLines={["Projenizi", "konuşalım."]}
        description="Ücretsiz keşif ve fizibilite için formu doldurun ya da doğrudan bize ulaşın. Ekibimiz en kısa sürede size dönüş yapacaktır."
      />

      <section className="relative bg-[#0A0A0A] pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="space-y-8">
                <InfoRow icon={Mail} label="E-posta" value={CONTACT.email} href={`mailto:${CONTACT.email}`} />
                <InfoRow icon={Phone} label="Telefon" value={CONTACT.phone} href={`tel:${CONTACT.phoneRaw}`} />
                <InfoRow icon={Instagram} label="Instagram" value={CONTACT.instagram} href={CONTACT.instagramUrl} />
                <InfoRow icon={MapPin} label="Adres" value={CONTACT.address} />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 rounded-3xl overflow-hidden border border-white/10 h-64">
                <iframe
                  title="Renewolt Konum"
                  src="https://maps.google.com/maps?q=Samanda%C4%9F%20Hatay&t=&z=12&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-full grayscale contrast-125 opacity-80"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="relative p-8 sm:p-10 rounded-3xl bg-[#121212] border border-white/10 overflow-hidden">
                <div className="absolute -top-24 -right-24 w-56 h-56 bg-[#5CC528]/10 rounded-full blur-3xl" />
                {sent ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative flex flex-col items-center text-center py-16"
                    data-testid="contact-success"
                  >
                    <CheckCircle2 size={56} className="text-[#5CC528] mb-6" />
                    <h3 className="font-display font-light text-3xl text-white mb-3">Teşekkürler!</h3>
                    <p className="text-neutral-400 max-w-sm">
                      Mesajınız bize ulaştı. En kısa sürede {CONTACT.email} veya telefon üzerinden dönüş yapacağız.
                    </p>
                    <button
                      onClick={() => setSent(false)}
                      data-testid="contact-new-message"
                      className="mt-8 text-sm text-[#5CC528] hover:underline"
                    >
                      Yeni mesaj gönder
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="relative space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-sm text-neutral-400 mb-2 block">Ad Soyad *</label>
                        <input data-testid="contact-name" className={inputCls} value={form.name} onChange={update("name")} placeholder="Adınız" />
                      </div>
                      <div>
                        <label className="text-sm text-neutral-400 mb-2 block">E-posta *</label>
                        <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={update("email")} placeholder="ornek@mail.com" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-sm text-neutral-400 mb-2 block">Telefon</label>
                        <input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={update("phone")} placeholder="05xx xxx xx xx" />
                      </div>
                      <div>
                        <label className="text-sm text-neutral-400 mb-2 block">Konu</label>
                        <input data-testid="contact-subject" className={inputCls} value={form.subject} onChange={update("subject")} placeholder="Çatı GES teklifi" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-neutral-400 mb-2 block">Mesajınız *</label>
                      <textarea data-testid="contact-message" rows={5} className={inputCls + " resize-none"} value={form.message} onChange={update("message")} placeholder="Projeniz hakkında kısa bilgi verin..." />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit"
                      className="inline-flex items-center justify-center gap-2 bg-[#5CC528] text-black font-semibold px-8 py-4 rounded-full hover:bg-[#4AA020] transition-colors duration-200 disabled:opacity-60 w-full sm:w-auto"
                    >
                      {loading ? <><Loader2 size={18} className="animate-spin" /> Gönderiliyor...</> : <>Mesajı Gönder <Send size={17} /></>}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
